import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import ReportIssueButton from '../components/issues/ReportIssueButton';
import {
  availableWorkspaces,
  setWorkspace,
  WORKSPACE_ROUTES,
} from '../utils/workspace';

/**
 * The post-authentication workspace selector: Field (שטח) or Lab (מעבדה).
 *
 * Full-screen and OUTSIDE Layout, like /change-password: it is a decision,
 * not a page within the app. It comes right after every sign-in (Login.js
 * clears the stored choice) and from the account menu's switcher. Lab opens
 * Home, the isolates hub. See utils/workspace.js for why a workspace is
 * deliberately not a permission.
 */

const ICONS = { field: '🌲', lab: '🧫' };

// One accent per card, so the two are distinguishable at a glance on a
// phone in sunlight rather than only by their text.
const ACCENTS = {
  field: 'var(--c-primary-2)',
  lab: 'var(--c-info-2)',
};

export default function Workspace() {
  const { t } = useTranslation('workspace');
  const { user } = useAuth();
  const navigate = useNavigate();

  const available = availableWorkspaces(user);

  const choose = (workspace) => {
    setWorkspace(workspace);
    navigate(WORKSPACE_ROUTES[workspace], { replace: true });
  };

  // Nothing to choose between (the Phase 6 roles): Home, as landingRedirect
  // would have sent them, rather than a chooser with no cards on it.
  if (available.length === 0) return <Navigate to="/" replace />;

  return (
    <div style={styles.page}>
      <div style={styles.inner}>
        {/* Report issue, as every page's header has. */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <ReportIssueButton style={styles.report} />
        </div>
        <h1 style={styles.title}>{t('title')}</h1>
        <p style={styles.subtitle}>{t('subtitle')}</p>

        <div style={styles.grid}>
          {available.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => choose(key)}
              title={t(`tip.${key}`)}
              style={{ ...styles.card, borderTopColor: ACCENTS[key] }}
            >
              <span style={styles.icon} aria-hidden="true">{ICONS[key]}</span>
              <span style={styles.tagline}>{t(`${key}.tagline`)}</span>
              <span style={{ ...styles.name, color: ACCENTS[key] }}>
                {t(`${key}.name`)}
              </span>
              <span style={styles.description}>{t(`${key}.description`)}</span>
              <span style={{ ...styles.enter, background: ACCENTS[key] }}>
                {t('enter')}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--c-bg-subtle-2)',
    padding: 24,
    boxSizing: 'border-box',
  },
  inner: { width: '100%', maxWidth: 720 },
  report: {
    background: 'var(--c-surface)', color: 'var(--c-text-fg)', border: '1px solid var(--c-border)', borderRadius: 999,
    padding: '6px 14px', fontSize: 13, cursor: 'pointer', minHeight: 40,
  },
  title: {
    margin: '0 0 8px',
    fontSize: 30,
    textAlign: 'center',
    color: 'var(--c-text-2-fg)',
  },
  subtitle: {
    margin: '0 0 32px',
    textAlign: 'center',
    color: 'var(--c-text-muted-3-fg)',
    fontSize: 16,
  },
  grid: {
    display: 'grid',
    // auto-fit rather than two fixed columns: on a phone -- which is
    // exactly where Field Mode is chosen -- these must stack, not shrink.
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 20,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    padding: '28px 22px',
    background: 'var(--c-surface)',
    border: '1px solid var(--c-border-2)',
    borderTop: '4px solid',
    borderRadius: 12,
    cursor: 'pointer',
    textAlign: 'center',
    font: 'inherit',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    // 44px is the accepted floor for a gloved or gloveless thumb; these are
    // far above it, deliberately.
    minHeight: 240,
  },
  icon: { fontSize: 40, lineHeight: 1 },
  tagline: {
    fontSize: 12,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--c-text-faint-5-fg)',
  },
  name: { fontSize: 22, fontWeight: 700 },
  description: { fontSize: 14, color: 'var(--c-text-muted-3-fg)', lineHeight: 1.5, flex: 1 },
  enter: {
    marginTop: 4,
    padding: '10px 26px',
    borderRadius: 6,
    color: 'var(--c-on-accent)',
    fontWeight: 600,
    fontSize: 15,
  },
};
